import { useEffect, useState } from "react";

import {
  TextArea,
  RadioButton,
  Typography,
  Select,
  Button,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models/Common/CodeBook";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { getInstitutionById } from "../../api/Institution/InstitutionApi";
import {
  getRequest,
  getLanguageCourseDecision,
  saveLanguageCourseDecision,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
  InstitutionSelect,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { ValidationFailure, Role } from "../../models";
import { Institution } from "../../models/Institution";
import {
  Request,
  RequestStatus,
  RequestType,
  LanguageCourseDecision,
  LanguageCourseOption,
  LanguageCourseDecisionConsentStatus,
  LanguageCourseDecisionStatus,
} from "../../models/Request";
import { ClaimType } from "../../models/User";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { MsgUtils } from "../../utils";

export function LanguageCourseDecisionEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const createDefault = (): Request => {
    return {
      id: 0,
      requestUID: "",
      code: "",
      statusId: RequestStatus.Draft,
      schoolYearId: 0,
      type: RequestType.RegularEnrollment,
      createdByAdmin: true,
      isOutsideEnrollmentArea: false,
      step: 0,
      subStep: 0,
      parentRemark: null,
    };
  };
  const createDefaultDecision = (): LanguageCourseDecision => {
    return {
      id: 0,
      requestId: +id,
      isConsentSent: false,
      languageCourseOptionId: LanguageCourseOption.SupplementaryCourse,
    };
  };
  const [item, setItem] = useState<Request>(createDefault());
  const [decision, setDecision] = useState<LanguageCourseDecision>(
    createDefaultDecision()
  );
  const [primaryInstitution, setPrimaryInstitution] = useState<
    Institution | undefined
  >();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [isDirty, setIsDirty] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      setIsEditing(true); // Assume we don't get one.
      if (id && id !== "0") {
        var result = await getLanguageCourseDecision(+id);
        if (result) {
          setDecision(result);
          setIsEditing(false);
          if (result.request) {
            setItem(result.request);
          }
        } else {
          var request = await getRequest(+id);
          if (request) {
            setItem(request);
          }
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    (async () => {
      if (
        item.institutionId &&
        item.schoolYearId > 0 &&
        (!primaryInstitution || primaryInstitution.id !== item.institutionId)
      ) {
        setPrimaryInstitution(
          await getInstitutionById(item.institutionId, item.schoolYearId)
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.institutionId, item.schoolYearId]);

  const canSave = (): boolean => {
    return (
      ((auth.currentRole?.role === Role.CountyAdministrator ||
        auth.currentRole?.role === Role.CountyLeader ||
        (item.countyCommitteeId &&
          auth
            .getCommitteeMembershipByCommitteeId(item.countyCommitteeId)
            ?.some((v) => v.isPresident)) ||
        auth.currentRole?.role === Role.CAS ||
        auth.currentRole?.role === Role.Helpdesk) &&
        !decision.isConsentSent) ||
      ((auth.currentRole?.role === Role.MinistryAdministrator ||
        auth.currentRole?.role === Role.CAS ||
        auth.currentRole?.role === Role.Helpdesk) &&
        decision.isConsentSent)
    );
  };
  const handleOptionChange = (option: LanguageCourseOption) => {
    const newDecision: LanguageCourseDecision = {
      ...decision,
      languageCourseOptionId: option,
      institutionId:
        option === LanguageCourseOption.PreparatoryCourse
          ? decision.institutionId
          : undefined,
    };
    handleUpdate(newDecision);
  };
  const handleInstitutionChange = (val: number) => {
    const newDecision: LanguageCourseDecision = {
      ...decision,
      institutionId: !isNaN(val) ? val : undefined,
    };
    handleUpdate(newDecision);
  };
  const handleDecisionChange = (val?: string) => {
    const newDecision: LanguageCourseDecision = {
      ...decision,
      decision: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(newDecision);
  };
  const handleSendClicked = async () => {
    const newDecision: LanguageCourseDecision = {
      ...decision,
      isConsentSent: true,
    };
    const result = await saveLanguageCourseDecision(newDecision);
    if (result) {
      setErrors(result.validationFailures);
      if (result.isValid && result.result) {
        setDecision(result.result);
        setIsEditing(false);
        if (result.result.request) {
          setItem(result.result.request);
        }
      }
    }
  };
  const handleMinistryDecisionChange = (val?: string) => {
    const newDecision: LanguageCourseDecision = {
      ...decision,
      ministryDecision: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(newDecision);
  };
  const handleConsentStatusChange = (val: number) => {
    const newDecision: LanguageCourseDecision = {
      ...decision,
      consentStatusId: !isNaN(val) ? val : undefined,
    };
    handleUpdate(newDecision);
  };
  const handleMinistryExplanationChange = (val?: string) => {
    const newDecision: LanguageCourseDecision = {
      ...decision,
      ministryExplanation: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(newDecision);
  };

  const handleUpdate = (val: LanguageCourseDecision) => {
    setDecision(val);
    dispatch(setIsEdited());
    setIsDirty(true);
  };

  const getEnrollmentDecisionConclusionList = (): CodeBook[] => {
    var arr = MsgUtils.getEnumToCodeBook(
      LanguageCourseDecisionConsentStatus,
      "languageCourseDecisionConsentStatus"
    );
    if (decision && decision.languageCourseOptionId) {
      switch (decision.languageCourseOptionId) {
        case LanguageCourseOption.PreparatoryCourse:
          arr = arr.filter(
            (v) =>
              v.id ===
                LanguageCourseDecisionConsentStatus.PreparatoryCourseApproved ||
              v.id ===
                LanguageCourseDecisionConsentStatus.PreparatoryCourseRejected
          );
          break;
        case LanguageCourseOption.SupplementaryCourse:
          arr = arr.filter(
            (v) =>
              v.id ===
                LanguageCourseDecisionConsentStatus.SupplementaryCourseApproved ||
              v.id ===
                LanguageCourseDecisionConsentStatus.SupplementaryCourseRejected
          );
          break;
      }
    }
    return arr;
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        pageTitle={t("languageCourseDecision.pageTitle")}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard>
        <Row>
          <Col md={12}>
            <Typography>
              {t("languageCourseDecision.countyDecision")}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}></Col>
          <Col md={10}>
            <RadioButton
              ariaLabel={t("languageCourseOption.SupplementaryCourse")}
              label={t("languageCourseOption.SupplementaryCourse")}
              checked={
                decision.languageCourseOptionId ===
                LanguageCourseOption.SupplementaryCourse
              }
              name={"no"}
              onChange={(e) =>
                handleOptionChange(LanguageCourseOption.SupplementaryCourse)
              }
              disabled={
                !(
                  auth.currentRole?.role === Role.CountyAdministrator ||
                  auth.currentRole?.role === Role.CountyLeader ||
                  (item.countyCommitteeId &&
                    auth
                      .getCommitteeMembershipByCommitteeId(
                        item.countyCommitteeId
                      )
                      ?.some((v) => v.isPresident)) ||
                  auth.currentRole?.role === Role.CAS ||
                  auth.currentRole?.role === Role.Helpdesk
                ) ||
                (item.languageCourseDecisionStatus &&
                  item.languageCourseDecisionStatus >
                    LanguageCourseDecisionStatus.SupplementaryPreparatoryCourseRejected)
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={2}></Col>
          <Col md={10}>
            <RadioButton
              ariaLabel={t("languageCourseOption.PreparatoryCourse")}
              label={t("languageCourseOption.PreparatoryCourse")}
              checked={
                decision.languageCourseOptionId ===
                LanguageCourseOption.PreparatoryCourse
              }
              name={"no"}
              onChange={(e) =>
                handleOptionChange(LanguageCourseOption.PreparatoryCourse)
              }
              disabled={
                !(
                  auth.currentRole?.role === Role.CountyAdministrator ||
                  auth.currentRole?.role === Role.CountyLeader ||
                  (item.countyCommitteeId &&
                    auth
                      .getCommitteeMembershipByCommitteeId(
                        item.countyCommitteeId
                      )
                      ?.some((v) => v.isPresident)) ||
                  auth.currentRole?.role === Role.CAS ||
                  auth.currentRole?.role === Role.Helpdesk
                ) ||
                (item.languageCourseDecisionStatus &&
                  item.languageCourseDecisionStatus >
                    LanguageCourseDecisionStatus.SupplementaryPreparatoryCourseRejected)
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}></Col>
          <Col md={8}>
            <InstitutionSelect
              name={nameof<typeof decision>("institutionId")}
              showUser={false}
              countyId={
                auth.currentRole?.claimType === ClaimType.Global
                  ? undefined
                  : item.countyId
              }
              onlyByEnrollmentArea={false}
              excludeHandicapped={false}
              excludePrivate={false}
              excludeSatellite={false}
              value={decision.institutionId}
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              disabled={
                decision.languageCourseOptionId !==
                  LanguageCourseOption.PreparatoryCourse ||
                !(
                  auth.currentRole?.role === Role.CountyAdministrator ||
                  auth.currentRole?.role === Role.CountyLeader ||
                  (item.countyCommitteeId &&
                    auth
                      .getCommitteeMembershipByCommitteeId(
                        item.countyCommitteeId
                      )
                      ?.some((v) => v.isPresident)) ||
                  auth.currentRole?.role === Role.CAS ||
                  auth.currentRole?.role === Role.Helpdesk
                ) ||
                (item.languageCourseDecisionStatus &&
                  item.languageCourseDecisionStatus >
                    LanguageCourseDecisionStatus.SupplementaryPreparatoryCourseRejected)
              }
              errorText={
                errors?.find((e) => e.propertyName === "InstitutionId")?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={2}></Col>
          <Col md={10}>
            <RadioButton
              ariaLabel={t("languageCourseOption.NotRequired")}
              label={t("languageCourseOption.NotRequired")}
              checked={
                decision.languageCourseOptionId ===
                LanguageCourseOption.NotRequired
              }
              name={"no"}
              onChange={(e) =>
                handleOptionChange(LanguageCourseOption.NotRequired)
              }
              disabled={
                !(
                  auth.currentRole?.role === Role.CountyAdministrator ||
                  auth.currentRole?.role === Role.CountyLeader ||
                  (item.countyCommitteeId &&
                    auth
                      .getCommitteeMembershipByCommitteeId(
                        item.countyCommitteeId
                      )
                      ?.some((v) => v.isPresident)) ||
                  auth.currentRole?.role === Role.CAS ||
                  auth.currentRole?.role === Role.Helpdesk
                ) ||
                (item.languageCourseDecisionStatus &&
                  item.languageCourseDecisionStatus >
                    LanguageCourseDecisionStatus.SupplementaryPreparatoryCourseRejected)
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel={t("languageCourseDecision.decision")}
              label={t("languageCourseDecision.decision")}
              value={decision.decision}
              onChange={(e) => handleDecisionChange(e.target.value)}
              disabled={
                !(
                  auth.currentRole?.role === Role.CountyAdministrator ||
                  auth.currentRole?.role === Role.CountyLeader ||
                  (item.countyCommitteeId &&
                    auth
                      .getCommitteeMembershipByCommitteeId(
                        item.countyCommitteeId
                      )
                      ?.some((v) => v.isPresident)) ||
                  auth.currentRole?.role === Role.CAS ||
                  auth.currentRole?.role === Role.Helpdesk
                ) ||
                (item.languageCourseDecisionStatus &&
                  item.languageCourseDecisionStatus >
                    LanguageCourseDecisionStatus.SupplementaryPreparatoryCourseRejected)
              }
              errorText={
                errors?.find((e) => e.propertyName === "Decision")?.message
              }
            />
          </Col>
        </Row>
        {(item.languageCourseDecisionStatus ===
          LanguageCourseDecisionStatus.PreparatoryCourseApproved ||
          item.languageCourseDecisionStatus ===
            LanguageCourseDecisionStatus.SupplementaryCourseApproved) &&
          !isDirty &&
          (auth.currentRole?.claimType === ClaimType.Institution ||
            auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk) && (
            <Row>
              <Col md={12}>
                <Button disabled={false} onClick={handleSendClicked}>
                  {t("languageCourseDecision.send")}
                </Button>
              </Col>
            </Row>
          )}
        {auth.currentRole?.claimType === ClaimType.Global &&
          decision &&
          decision.isConsentSent && (
            <>
              <Row>
                <Col md={12}>
                  <TextArea
                    ariaLabel={t("languageCourseDecision.ministryDecision")}
                    label={t("languageCourseDecision.ministryDecision")}
                    value={decision.ministryDecision}
                    onChange={(e) =>
                      handleMinistryDecisionChange(e.target.value)
                    }
                    disabled={false}
                    errorText={
                      errors?.find((e) => e.propertyName === "MinistryDecision")
                        ?.message
                    }
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Select
                    label={t("languageCourseDecision.consentStatusId")}
                    selectData={codeBookToSelectData(
                      getEnrollmentDecisionConclusionList()
                    )}
                    value={decision.consentStatusId}
                    onChange={(e) =>
                      handleConsentStatusChange(Number.parseInt(e.target.value))
                    }
                    disabled={false}
                    errorText={
                      errors?.find((e) => e.propertyName === "ConsentStatusId")
                        ?.message
                    }
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <TextArea
                    ariaLabel={t("languageCourseDecision.ministryExplanation")}
                    label={t("languageCourseDecision.ministryExplanation")}
                    value={decision.ministryExplanation}
                    onChange={(e) =>
                      handleMinistryExplanationChange(e.target.value)
                    }
                    disabled={false}
                    errorText={
                      errors?.find(
                        (e) => e.propertyName === "MinistryExplanation"
                      )?.message
                    }
                  />
                </Col>
              </Row>
            </>
          )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={canSave() ? "show" : "hide"}
        onClickCancel={history.goBack}
        handleSave={async () => saveLanguageCourseDecision(decision)}
        setSaveErrors={setErrors}
        redirectUrlSaveOK={() => {
          history.goBack();
          return "";
        }}
        showCustom={false}
      />
    </ContentArea>
  );
}
